import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, FileSpreadsheet } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) => `R ${Number(n || 0).toFixed(2)}`;

const defaultMonth = () => {
  // Default to last completed month (buying-group report is submitted after month-end).
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function BuyingGroupReport() {
  const [monthFrom, setMonthFrom] = useState(defaultMonth());
  const [monthTo, setMonthTo] = useState(defaultMonth());
  const [supplierId, setSupplierId] = useState("all");
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/suppliers`);
        setSuppliers((res.data || []).sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      } catch { /* ignore */ }
    })();
  }, []);

  const rangeParams = () => {
    // Normalise so from <= to; treat single-month selections as a 1-month range.
    const from = monthFrom;
    const to = monthTo || monthFrom;
    const [lo, hi] = from <= to ? [from, to] : [to, from];
    const params = { month_from: lo, month_to: hi };
    if (supplierId && supplierId !== "all") params.supplier_id = supplierId;
    return params;
  };

  const loadPreview = async () => {
    setLoading(true);
    setData(null);
    try {
      const res = await axios.get(`${API}/reports/buying-group`, { params: rangeParams() });
      setData(res.data);
      if ((res.data.rows || []).length === 0) toast.info("No GRVs found for that period");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load report");
    } finally {
      setLoading(false);
    }
  };

  const downloadXlsx = async () => {
    setDownloading(true);
    try {
      const params = rangeParams();
      const res = await axios.get(`${API}/reports/buying-group/xlsx`, { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const label = params.month_from === params.month_to
        ? params.month_from
        : `${params.month_from}_to_${params.month_to}`;
      a.download = `BuyingGroup_${label}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (err) {
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="buying-group-report-page">
      <div className="flex items-center gap-3">
        <FileSpreadsheet className="h-7 w-7 text-emerald-600" />
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Buying Group Report</h1>
          <p className="text-sm text-slate-500">Monthly XLSX return matching your buying group&apos;s template.</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label>From month</Label>
            <Input
              type="month"
              value={monthFrom}
              onChange={(e) => setMonthFrom(e.target.value)}
              data-testid="buying-group-month-from-input"
            />
          </div>
          <div>
            <Label>To month</Label>
            <Input
              type="month"
              value={monthTo}
              onChange={(e) => setMonthTo(e.target.value)}
              data-testid="buying-group-month-to-input"
            />
            <p className="text-xs text-slate-500 mt-1">Same as &ldquo;From&rdquo; for one month only</p>
          </div>
          <div>
            <Label>Supplier (optional)</Label>
            <Select value={supplierId} onValueChange={setSupplierId}>
              <SelectTrigger data-testid="buying-group-supplier-select"><SelectValue placeholder="All suppliers" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All suppliers</SelectItem>
                {suppliers.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Button onClick={loadPreview} disabled={loading} className="flex-1" data-testid="buying-group-preview-btn">
              {loading ? "Loading…" : "Preview"}
            </Button>
            <Button onClick={downloadXlsx} disabled={downloading} variant="secondary" className="flex-1" data-testid="buying-group-download-btn">
              <Download className="h-4 w-4 mr-2" /> {downloading ? "…" : "XLSX"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {data && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span>{data.totals.row_count} row{data.totals.row_count === 1 ? "" : "s"}</span>
              <span className="font-mono text-sm text-slate-600">
                Excl {money(data.totals.amount_excl)} · VAT {money(data.totals.vat)} · <span className="font-bold text-slate-900">Incl {money(data.totals.amount_incl)}</span>
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Month</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Account #</TableHead>
                  <TableHead>GRV #</TableHead>
                  <TableHead className="text-right">Amount Excl</TableHead>
                  <TableHead className="text-right">VAT</TableHead>
                  <TableHead className="text-right">Amount Incl</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r, idx) => (
                  <TableRow key={idx} data-testid={`buying-group-row-${idx}`}>
                    <TableCell className="whitespace-nowrap">{r.date}</TableCell>
                    <TableCell>{r.month}</TableCell>
                    <TableCell className="max-w-[220px] truncate" title={r.supplier_name}>{r.supplier_name}</TableCell>
                    <TableCell className="font-mono text-xs">{r.invoice_number}</TableCell>
                    <TableCell className="font-mono text-xs">{r.account_number}</TableCell>
                    <TableCell className="font-mono text-xs">{r.grv_number}</TableCell>
                    <TableCell className="text-right font-mono">{money(r.amount_excl)}</TableCell>
                    <TableCell className="text-right font-mono">{money(r.vat)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{money(r.amount_incl)}</TableCell>
                  </TableRow>
                ))}
                {data.rows.length > 0 && (
                  <TableRow className="bg-amber-50 font-bold">
                    <TableCell colSpan={6} className="text-right">Totals</TableCell>
                    <TableCell className="text-right font-mono">{money(data.totals.amount_excl)}</TableCell>
                    <TableCell className="text-right font-mono">{money(data.totals.vat)}</TableCell>
                    <TableCell className="text-right font-mono">{money(data.totals.amount_incl)}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
